import { useEffect } from "react";

import { Download, Cancel } from "@mui/icons-material";
import { Chip, IconButton } from "@mui/material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import { MainLayout } from "@shared/ui/MainLayout";
import MDBox from "@vendor/components/MDBox";
import { useLoading } from "@shared/hooks/useLoading";
import { DataGridTable } from "@shared/ui/DataGridTable";
import { ErrorAlert } from "@shared/ui/alerts";
import { getFormatDate } from "@shared/utils/getFormatDate";
import { CustomToolbarReloadComponent } from "@shared/ui/MuiTable/CustomToolbarReloadComponent";

import type { GridColDef } from "@mui/x-data-grid";

import { PreInvoiceItem } from "../components/PreInvoiceItem";
import { usePreInvoiceList } from "../hooks/usePreInvoiceList";
import { usePreInvoiceUser } from "../hooks/usePreInvoiceUser";
import { cancelPreInvoice, downloadPreInvoice } from "../api/preInvoiceApi";
import { PREINVOICE_STATUS, STATUS_CHIP_META } from "../constants";

export const PreInvoicePage = () => {
  const { loading, thisLoading, notLoading } = useLoading();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    data: preInvoiceList = [],
    isFetching,
    isError,
    refetch,
  } = usePreInvoiceList();
  const { data: userData } = usePreInvoiceUser();

  // Reutiliza el query compartido para recargar tras cancelar/generar.
  const reloadList = () => refetch();

  useEffect(() => {
    if (isError) ErrorAlert(t("errorTitle"), t("errorToGetPreInvoiceList")).then();
  }, [isError]);

  const onCancelPreInvoice = (preInvoiceId: number) => {
    const preInvoiceStatus = preInvoiceList.find((item) => item.id === preInvoiceId);
    if (preInvoiceStatus!.status !== PREINVOICE_STATUS.CANCELLED) {
      Swal.fire({
        title: t("cancelledPreInvoiceTitle"),
        text: t("cancelledPreInvoiceQuestion"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("cancel"),
        cancelButtonText: t("exit"),
      }).then((result) => {
        if (result.isConfirmed) {
          cancelPreInvoice(preInvoiceId)
            .then(() => {
              Swal.fire({
                title: t("successfulCancellationTitle"),
                text: t("successfulCancellationMessage"),
                icon: "success",
                confirmButtonText: "OK",
              });
            })
            .catch((error) => {
              console.log("error => ", error);
              Swal.fire({
                title: t("cancelledPreInvoiceTitle"),
                text: t("errorCancelledPreInvoice"),
                icon: "error",
                confirmButtonText: "OK",
              });
            })
            .finally(() => {
              reloadList();
            });
        }
      });
    } else {
      Swal.fire({
        title: t("cancelInvoice"),
        text: t("invoiceIsAlreadyCanceled"),
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const onDownload = (id: number) => {
    thisLoading();
    downloadPreInvoice(id)
      .then(({ data, headers }) => {
        const link = document.createElement("a");
        link.target = "_blank";
        link.download = `${t("preInvoice")}_${userData?.firstName}_${userData?.lastName}${getFormatDate()}.pdf`;
        link.href = window.URL.createObjectURL(
          new Blob([data], {
            type: headers["content-type"] as string | undefined,
          })
        );
        link.click();
        notLoading();
      })
      .catch(() => notLoading());
  };

  const status = (value: string) => {
    const meta = STATUS_CHIP_META[value];
    if (!meta) return null;
    return <Chip label={t(meta.labelKey)} sx={{ backgroundColor: meta.color, color: "#fff" }} />;
  };

  const columns: GridColDef[] = [
    { field: "createdAt", headerName: t("createdAt"), flex: 1 },
    {
      field: "detailsClient",
      headerName: t("client"),
      flex: 1,
      valueGetter: (_value: unknown, row: { details?: { client?: string }[] }) =>
        (row.details ?? []).map((item) => item.client).join(",") + " ",
    },
    {
      field: "detailsDescription",
      headerName: t("description"),
      flex: 1,
      valueGetter: (_value: unknown, row: { details?: Record<string, unknown>[] }) =>
        (row.details ?? []).map((item: Record<string, unknown>) => item.description).join(",") + " ",
    },
    {
      field: "detailsModalityWork",
      headerName: t("modalityWork"),
      flex: 1,
      valueGetter: (_value: unknown, row: { details?: Record<string, unknown>[] }) =>
        (row.details ?? []).map((item: Record<string, unknown>) => item.modalityWork).join(",") + " ",
    },
    {
      field: "detailsUnitPrice",
      headerName: t("unitPrice"),
      flex: 1,
      valueGetter: (value, row) => (row.details ?? []).map((item: Record<string, unknown>) => item.priceUnit).join(",") + " ",
    },
    { field: "hours", headerName: t("hours"), flex: 1 },
    { field: "month", headerName: t("month"), flex: 1 },
    { field: "year", headerName: t("year"), flex: 1 },
    { field: "benefitDiscount", headerName: t("discount"), flex: 1 },
    { field: "total", headerName: t("subTotal"), flex: 1 },
    { field: "currency", headerName: t("currency"), flex: 1 },
    {
      field: "status",
      headerName: t("status"),
      flex: 1,
      renderCell: (params) => status(params.value),
    },
    {
      field: "download",
      headerName: t("download"),
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton onClick={() => onDownload(params.row.id)}>
          <Download />
        </IconButton>
      ),
    },
    {
      field: "cancel",
      headerName: t("cancel"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.status !== PREINVOICE_STATUS.CREATED) {
          return "";
        }
        return (
          <IconButton
            onClick={() => {
              onCancelPreInvoice(params.row.id);
            }}
          >
            <Cancel />
          </IconButton>
        );
      },
    },
  ];

  const toolbarActions = (
    <CustomToolbarReloadComponent title={t("reloadData")} onClickAction={reloadList} />
  );

  return (
    <MainLayout loading={loading || isFetching}>
      <MDBox py={3}>
        <PreInvoiceItem
          notLoading={notLoading}
          thisLoading={thisLoading}
          getListPreInvoice={reloadList}
          userData={userData}
        />
        <DataGridTable
          rows={preInvoiceList}
          title={t("preInvoice")}
          columns={columns}
          language={language}
          search={false}
          download={false}
          toolbarActions={toolbarActions}
        />
      </MDBox>
    </MainLayout>
  );
};
